/*
 * Copyright (c) 2020 Little Robot Friends, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, {useState, useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {isEmpty, isLoaded} from 'react-redux-firebase'

import {FlexGrid, FlexGridItem} from 'baseui/flex-grid'
import {Block} from 'baseui/block'
import {Input} from 'baseui/input'
import {Select} from 'baseui/select'
import {SIZE} from 'baseui/spinner'

import {InputList, CenteredCard, PaginatedContent, Page, PageTitle} from '../../components/app'
import LoadingSpinner from '../../components/app/loading-spinner'
// import PageLoading from '../../components/app/page-loading'
import {updateStudentData} from '../persistence/student-actions'
import {updateUserData} from '../persistence/user-actions'
import CenteredBlock from '../../components/app/centered-block'

const Onboarding = () => {
  const [studentInfo, setStudentInfo] = useState(null)
  const [userInfo, setUserInfo] = useState(null)
  const [isReady, setIsReady] = useState(false)
  const dispatch = useDispatch()
  const {enrollments} = useSelector(state => state.firestore?.ordered)
  const user = useSelector(state => state.firestore?.data?.user)
  const auth = useSelector(state => state.firebase.auth)

  const ageOptions = [
    {id: '6orY', label: '6 or younger'},
    {id: '7', label: '7 years old'},
    {id: '8', label: '8 years old'},
    {id: '9', label: '9 years old'},
    {id: '10', label: '10 years old'},
    {id: '11', label: '11 years old'},
    {id: '12orO', label: '12 or older'},
  ]
  const genderOptions = [
    {id: 'boy', label: 'boy'},
    {id: 'girl', label: 'girl'},
    {id: 'not', label: 'rather not say'},
  ]
  const skillOptions = [
    {id: 'new', label: 'never before'},
    {id: 'beginner', label: 'once or twice'},
    {id: 'intermediate', label: 'a couple times'},
    {id: 'expert', label: 'all the time'},
  ]
  const deviceTypeOptions = [
    {id: 'tablet', label: 'tablet'},
    {id: 'laptop', label: 'laptop'},
    {id: 'desktop', label: 'desktop'},
    {id: 'phone', label: 'mobile phone'},
  ]
  const deviceAgeOptions = [
    {id: 'new', label: 'less than 2 years'},
    {id: 'newish', label: '2-3 years old'},
    {id: 'oldish', label: '4-5 years old'},
    {id: 'old', label: 'older than 5 years'},
  ]
  const emailFrequencyOptions = [
    {id: 'weekly', label: 'weekly'},
    {id: 'biweekly', label: 'bi-weekly'},
    {id: 'monthly', label: 'monthly'},
    {id: 'never', label: 'never'},
  ]

  // update our local array of student info
  const studentNameChanged = (idx, val, e) => {
    studentInfoChanged(idx, 'studentName', val, e)
  }

  const studentInfoChanged = (idx, key, val, e) => {
    const updatedInfo = [...studentInfo]
    updatedInfo[idx][key] = val
    setStudentInfo(updatedInfo)
  }

  const handleDone = async () => {
    setIsReady(false)
    // commit the data
    enrollments.forEach(async (student, idx) => {
      const info = studentInfo[idx]
      await dispatch(updateStudentData(info, student.id))
    })
    // update the user (set onboarding flag, also update email info)
    const info = {onboardingComplete: true, ...userInfo}
    await dispatch(updateUserData(info, auth.uid))
    setIsReady(true)
  }

  const handleEmailChanged = val => {
    // console.log('email changed:', val)
    const updatedInfo = {...userInfo}
    updatedInfo.communicationAddress = val
    setUserInfo(updatedInfo)
  }

  const handleEmailFreqChanged = val => {
    // console.log('email freq changed:', e)
    const updatedInfo = {...userInfo}
    updatedInfo.communicationFrequency = val
    setUserInfo(updatedInfo)
  }

  const FirstPage = () => {
    return (
      <Page key="first" title="Welcome to Secret of the Source Code">
        There are a couple quick steps needed before you begin...
      </Page>
    )
  }

  const LastPage = () => {
    return (
      <Page key="last" title="All done!">
        You're all set!
        <br />
        Time to begin your search for the 'Secret of the Source Code'!
      </Page>
    )
  }

  const NamePage = () => {
    return (
      <Page key="names">
        <PageTitle>
          You registered <b>{studentInfo.length}</b> students.
        </PageTitle>
        Can you give us their name(s)?
        <InputList
          list={studentInfo.map(info => info.studentName)}
          onChange={studentNameChanged}
          placeholder="student name"
        />
      </Page>
    )
  }

  const ParentPage = () => {
    return (
      <Page key="parents" title="Great! One last thing...">
        We typically send out weekly emails to let parents, caregivers, and educators know how their
        coders are doing.
        <br />
        Would you like to make any changes to this?
        <Input
          value={userInfo.communicationAddress}
          onChange={e => {
            handleEmailChanged(e.target.value)
          }}
          overrides={{
            Root: {
              style: ({$theme}) => ({
                marginTop: '10px',
              }),
            },
          }}
        />
        <Select
          value={userInfo.communicationFrequency}
          options={emailFrequencyOptions}
          onChange={e => {
            handleEmailFreqChanged(e.value)
          }}
          overrides={{
            Root: {
              style: ({$theme}) => ({
                marginTop: '10px',
              }),
            },
          }}
        />
      </Page>
    )
  }

  const StudentInfoPages = () => {
    return studentInfo.map((info, idx) => (
      <Page key={`student${idx}`}>
        <PageTitle>
          Can you tell us a few things about <b>{info.studentName}</b>?
        </PageTitle>
        <FlexGrid flexGridColumnCount={2} flexGridColumnGap="scale800" flexGridRowGap="scale800">
          <FlexGridItem>
            What is their age?
            <Select
              value={info.studentAge}
              options={ageOptions}
              onChange={e => studentInfoChanged(idx, 'studentAge', e.value, e)}
            />
          </FlexGridItem>
          <FlexGridItem>
            What is their gender?
            <Select
              value={info.studentGender}
              options={genderOptions}
              onChange={e => studentInfoChanged(idx, 'studentGender', e.value, e)}
            />
          </FlexGridItem>
          <FlexGridItem>
            Have they coded before?
            <Select
              value={info.studentSkill}
              options={skillOptions}
              onChange={e => studentInfoChanged(idx, 'studentSkill', e.value, e)}
            />
          </FlexGridItem>
          <FlexGridItem>
            What is their main device?
            <Select
              value={info.studentDeviceType}
              options={deviceTypeOptions}
              onChange={e => studentInfoChanged(idx, 'studentDeviceType', e.value, e)}
            />
            <Select
              value={info.studentDeviceAge}
              options={deviceAgeOptions}
              onChange={e => studentInfoChanged(idx, 'studentDeviceAge', e.value, e)}
              overrides={{
                Root: {
                  style: ({$theme}) => ({
                    marginTop: '10px',
                  }),
                },
              }}
            />
          </FlexGridItem>
        </FlexGrid>
      </Page>
    ))
  }

  const pages = !isReady
    ? []
    : [FirstPage(), NamePage(), ...StudentInfoPages(), ParentPage(), LastPage()]

  useEffect(() => {
    if (!isEmpty(enrollments) && isLoaded(user)) {
      // setTimeout(() => setNameList(enrollments.map(item => item.studentName)))
      setTimeout(() => {
        setUserInfo({
          communicationAddress: user.communicationAddress ? user.communicationAddress : auth.email,
          communicationFrequency: user.communicationFrequency ? user.communicationFrequency : [],
        })
        setStudentInfo(
          enrollments.map(item => {
            return {
              studentName: item.studentName,
              studentAge: item.studentAge,
              studentGender: item.studentGender,
              studentSkill: item.studentSkill,
              studentDeviceType: item.studentDeviceType,
              studentDeviceAge: item.studentDeviceAge,
            }
          }),
        )
        setIsReady(true)
      }, 500)
    }
  }, [enrollments, user, auth])

  return (
    <CenteredBlock>
      <CenteredCard>
        {!isReady ? (
          <Block
            overrides={{
              Block: {
                style: ({$theme}) => ({
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                }),
              },
            }}
          >
            <LoadingSpinner $size={SIZE.large} />
          </Block>
        ) : (
          <PaginatedContent onDone={handleDone}>{pages}</PaginatedContent>
        )}
      </CenteredCard>
    </CenteredBlock>
  )
}

export default Onboarding
